import {isFeatureEnabled} from '@github-ui/feature-flags'
import {BetaLabel} from '@github-ui/lifecycle-labels/beta'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {AlertIcon} from '@primer/octicons-react'
import {Banner, Label, Link} from '@primer/react'
import type React from 'react'

import type {useCurrentBlob} from '../../../hooks/CurrentBlob'
import styles from './IssueTemplateBanner.module.css'

type IssueTemplateBannerProps = Pick<
  ReturnType<typeof useCurrentBlob>,
  'issueTemplate' | 'showIssueFormWarning' | 'isValidLegacyIssueTemplate'
> & {
  isLoggedIn: boolean
  helpUrl: string
}

export function IssueTemplateBanner(props: IssueTemplateBannerProps) {
  const {issueTemplate, showIssueFormWarning, isValidLegacyIssueTemplate, helpUrl, isLoggedIn} = props

  if (!isLoggedIn || (!issueTemplate && !isValidLegacyIssueTemplate && !showIssueFormWarning)) {
    return null
  }

  const lifecycleLabelNameEnabled = isFeatureEnabled('lifecycle_label_name_updates')

  let content: React.ReactNode = null

  let bannerType: 'success' | 'critical' | 'info' | 'warning' | undefined = undefined
  let bannerTitle: string = 'Info'
  const errorMessageLinkText: string[] = []

  if (issueTemplate) {
    if (issueTemplate.valid === false) {
      bannerType = 'critical'
      bannerTitle = 'Critical'
      if (issueTemplate.errors) {
        if (issueTemplate.errors.length === 1) {
          errorMessageLinkText.push('Learn more about this error.')
        } else {
          issueTemplate.errors.map((error, index) => {
            errorMessageLinkText.push(`Learn more about error ${index + 1}.`)
          })
        }
      }

      content = (
        <>
          <p>
            <AlertIcon />
            <strong>
              There {issueTemplate.errors?.length === 1 ? 'is a problem' : 'are some problems'} with this template
            </strong>
          </p>

          {issueTemplate.errors?.map((error, index) => {
            return (
              // eslint-disable-next-line @eslint-react/no-array-index-key
              <p key={`error-${index}`}>
                <VerifiedHTMLText html={error.message} />
                {'. '}
                <Link href={error.link} target="_blank">
                  {errorMessageLinkText[index]}
                </Link>
              </p>
            )
          })}
        </>
      )
    } else {
      if (issueTemplate.structured) {
        content = (
          <>
            {lifecycleLabelNameEnabled ? (
              <BetaLabel className="mr-2" />
            ) : (
              <Label variant="success" className={styles.Label}>
                Beta
              </Label>
            )}
            This file is used as an Issue Form template.{' '}
            <a href="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/orgs/community/discussions/categories/projects-and-issues">Give feedback.</a>
          </>
        )
      } else {
        content = 'This file is used as a markdown issue template.'
      }
    }
  } else if (showIssueFormWarning) {
    content = 'Issue form templates are not supported on private repositories.'
    bannerType = 'warning'
    bannerTitle = 'Warning'
  }

  return (
    <>
      {(issueTemplate || showIssueFormWarning) && (
        <Banner title={bannerTitle} variant={bannerType} className={styles.Banner}>
          {content}
        </Banner>
      )}
      {isValidLegacyIssueTemplate && <LegacyIssueTemplateBanner helpUrl={helpUrl} />}
    </>
  )
}

function LegacyIssueTemplateBanner({helpUrl}: {helpUrl: string}) {
  return (
    <Banner title="Warning" variant="warning" className={styles.Banner}>
      You are using an old version of issue templates. Please update to the new issue template workflow.{' '}
      <Link href={`${helpUrl}/articles/about-issue-and-pull-request-templates`} target="_blank" inline>
        Learn more about issue templates.
      </Link>
    </Banner>
  )
}
